import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ReplyThread from "./ReplyThread";
import ActionReasonDialog from "./ActionReasonDialog";
import { POST_LOCK_REASONS, POST_ADMIN_DECISION_REASONS } from "../utils/moderationReasons";
import {
  fetchThread,
  lockThread,
  removeThread,
  lockComment,
  removeComment,
  dismissPostReport,
} from "../services/api";
import { useAuth } from "./AuthContext";
import { isThreadEdited } from "../utils/isEdited";
import EditedTag from "./EditedTag";
import { useInstitution } from "./InstitutionContext";
import { userHasInstitutionRole } from "../utils/permissions";
import IconTextChip from "./IconTextChip";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import RuleIcon from "@mui/icons-material/Rule";

const buildCommentTree = (comments) => {
  const map = new Map();
  const roots = [];
  (comments || []).forEach((c) => {
    map.set(c.id, { ...c, replies: [] });
  });
  map.forEach((node) => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id).replies.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortTree = (nodes) => {
    nodes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    nodes.forEach((n) => sortTree(n.replies));
  };
  sortTree(roots);
  return roots;
};

export default function ModerationThreadDialog({ open, onClose, post }) {
  const { user, emailVerified } = useAuth();
  const verifiedUser = user && emailVerified;
  const { selectedInstitution } = useInstitution();
  const canModerate = userHasInstitutionRole(user, 'CommunityAdminTeam', selectedInstitution);
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lockThreadOpen, setLockThreadOpen] = useState(false);
  const [removeThreadOpen, setRemoveThreadOpen] = useState(false);
  const [lockCommentData, setLockCommentData] = useState(null);
  const [removeCommentData, setRemoveCommentData] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});

  const threadId = post?.type === "Thread" ? post.id : post?.thread_id;

  useEffect(() => {
    if (!open || !threadId) return;
    setLoading(true);
    fetchThread(threadId)
      .then((res) => setData(res))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [open, threadId]);

  const toggleExpanded = (id) => {
    setExpandedComments((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (!data) return;
    const expanded = {};
    if (post?.type === 'Comment') {
      let parentId = post.parent_id;
      while (parentId) {
        expanded[parentId] = true;
        const parent = data.comments.find((c) => c.id === parentId);
        parentId = parent?.parent_id;
      }
    }
    setExpandedComments(expanded);
  }, [data, post]);

  const handleLockThreadConfirm = async (reason) => {
    try {
      await lockThread(threadId, reason);
      setData((prev) => ({
        ...prev,
        thread: { ...prev.thread, is_locked: true, lock_reason: reason },
      }));
    } catch (err) {
      console.error("Failed to lock thread", err);
    } finally {
      setLockThreadOpen(false);
    }
  };

  const handleUnlockThread = async () => {
    try {
      await lockThread(threadId);
      setData((prev) => ({
        ...prev,
        thread: { ...prev.thread, is_locked: false, lock_reason: "" },
      }));
    } catch (err) {
      console.error("Failed to unlock thread", err);
    }
  };

  const handleRemoveThreadConfirm = async (reason) => {
    try {
      await removeThread(threadId, reason);
      setData((prev) => ({
        ...prev,
        thread: {
          ...prev.thread,
          is_removed: true,
          deletion_reason: reason,
          title: "[removed]",
          content: "[removed]",
        },
      }));
    } catch (err) {
      console.error("Failed to remove thread", err);
    } finally {
      setRemoveThreadOpen(false);
    }
  };

  const handleLockCommentConfirm = async (reason) => {
    if (!lockCommentData) return;
    try {
      await lockComment(lockCommentData.id, reason);
      setData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          c.id === lockCommentData.id ? { ...c, is_locked: true, lock_reason: reason } : c
        ),
      }));
    } catch (err) {
      console.error("Failed to lock comment", err);
    } finally {
      setLockCommentData(null);
    }
  };

  const handleUnlockComment = async (comment) => {
    try {
      await lockComment(comment.id);
      setData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          c.id === comment.id ? { ...c, is_locked: false, lock_reason: "" } : c
        ),
      }));
    } catch (err) {
      console.error("Failed to unlock comment", err);
    }
  };

  const handleRemoveCommentConfirm = async (reason) => {
    if (!removeCommentData) return;
    try {
      await removeComment(removeCommentData.id, reason);
      setData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          c.id === removeCommentData.id
            ? { ...c, is_removed: true, deletion_reason: reason, content: "[removed]", user: null }
            : c
        ),
      }));
    } catch (err) {
      console.error("Failed to remove comment", err);
    } finally {
      setRemoveCommentData(null);
    }
  };

  const handleDismissReport = async (id) => {
    try {
      await dismissPostReport(id);
    } catch (err) {
      console.error("Failed to dismiss report", err);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Post Moderation</DialogTitle>
      <DialogContent dividers>
        {loading || !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                border:
                  post.type === "Thread" ? `2px solid ${theme.palette.error.main}` : "none",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                {data.thread.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: data.thread.content }}
              />
              {isThreadEdited(data.thread) &&
                !['[deleted]', '[removed]'].includes((data.thread.content || '').trim()) && (
                  <>
                    {' '}
                    <EditedTag />
                  </>
                )}
            </Paper>
            {post.type === "Thread" && (
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                {user?.permissions?.includes("can_lock_post") && canModerate && (
                  <IconTextChip
                    icon={data.thread.is_locked ? <LockOpenIcon /> : <LockIcon />}
                    label={data.thread.is_locked ? "Unlock" : "Lock"}
                    color="warning"
                    onClick={() =>
                      data.thread.is_locked
                        ? handleUnlockThread()
                        : setLockThreadOpen(true)
                    }
                  />
                )}
                {user?.permissions?.includes("can_remove_post") &&
                  canModerate &&
                  !data.thread.is_removed && (
                    <IconTextChip
                      icon={<RemoveCircleIcon />}
                      label="Remove"
                      color="error"
                      onClick={() => setRemoveThreadOpen(true)}
                    />
                  )}
                {post.report_count > 0 && (
                  <IconTextChip
                    icon={<RuleIcon />}
                    label="Dismiss Report"
                    color="info"
                    onClick={() => handleDismissReport(post.id)}
                  />
                )}
              </Box>
            )}
            {buildCommentTree(data.comments).map((c) => (
              <ReplyThread
                key={c.id}
                reply={c}
                user={user}
                verifiedUser={verifiedUser}
                onLock={(comment) =>
                  comment.is_locked ? handleUnlockComment(comment) : setLockCommentData(comment)
                }
                onRemove={(comment) => setRemoveCommentData(comment)}
                threadDeleted={data.thread.is_deleted}
                threadLocked={data.thread.is_locked}
                threadRemoved={data.thread.is_removed}
                highlightId={post.type === "Comment" ? post.id : null}
                moderationMode
                onDismiss={handleDismissReport}
                expanded={!!expandedComments[c.id]}
                onToggle={() => toggleExpanded(c.id)}
              />
            ))}
            {user?.permissions?.includes("can_lock_post") && canModerate && (
              <ActionReasonDialog
                open={lockThreadOpen}
                onClose={() => setLockThreadOpen(false)}
                title="Lock Thread"
                reasons={POST_LOCK_REASONS}
                onConfirm={handleLockThreadConfirm}
              />
            )}
            {user?.permissions?.includes("can_remove_post") && canModerate && (
              <ActionReasonDialog
                open={removeThreadOpen}
                onClose={() => setRemoveThreadOpen(false)}
                title="Remove Thread"
                reasons={POST_ADMIN_DECISION_REASONS}
                onConfirm={handleRemoveThreadConfirm}
              />
            )}
            <ActionReasonDialog
              open={!!lockCommentData}
              onClose={() => setLockCommentData(null)}
              title="Lock Comment"
              reasons={POST_LOCK_REASONS}
              onConfirm={handleLockCommentConfirm}
            />
            <ActionReasonDialog
              open={!!removeCommentData}
              onClose={() => setRemoveCommentData(null)}
              title="Remove Comment"
              reasons={POST_ADMIN_DECISION_REASONS}
              onConfirm={handleRemoveCommentConfirm}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
